import { useEffect, useRef, useState } from "react";
import { PlaneIco } from "../../assests/ExportImageIcon";
import service from "../../services/service";
import endpoints from "../../services/endpoints";
// Same dropdown-under-input shell as AirportAutocomplete/TrainStationAutocomplete
// — reused as-is. GET /flights/search/airlines returns { name, airline_code }.
import "../airportAutocomplete/AirportAutocomplete.css";
import "./AirlineAutocomplete.css";

const AirlineAutocomplete = ({ value, onChange, placeholder = "Example: AA or American Airlines", className = "" }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery("");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const PANEL_HEIGHT = 280;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow);
  }, [open]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);

    clearTimeout(debounceRef.current);
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await service.get(
          endpoints.flights.searchAirlines,
          { airline: text.trim() },
          { silent: true }
        );
        setResults(data.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleSelect = (airline) => {
    onChange(airline);
    setQuery("");
    setOpen(false);
  };

  const showResults = open && query.trim().length >= 2;
  // Once an airline is picked, show it as a rich "chip" (icon + code + name)
  // instead of raw text — clicking it re-opens the plain search input so the
  // exact same debounced search flow above still drives picking a new one.
  const showChip = !open && !!value;

  return (
    <div className={`airlineAutocomplete ${className}`.trim()} ref={wrapperRef}>
      {showChip ? (
        <button type="button" className="airlineAutocomplete__chip" onClick={() => setOpen(true)}>
          <img src={PlaneIco} alt="" className="airlineAutocomplete__chipPlane" />
          <span className="airlineAutocomplete__chipCode">{value.airline_code}</span>
          <span className="airlineAutocomplete__chipName">{value.name}</span>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="airlineAutocomplete__chevron">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : (
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          autoFocus={open}
        />
      )}

      {showResults && (
        <div className={`airportAutocomplete__panel ${openUp ? "airportAutocomplete__panel--up" : ""}`}>
          {loading && <div className="airportAutocomplete__empty">Searching airlines...</div>}
          {!loading && results.length === 0 && (
            <div className="airportAutocomplete__empty">No airlines found</div>
          )}
          {!loading &&
            results.map((item) => (
              <button
                type="button"
                key={item.airline_code}
                className="airportAutocomplete__item"
                onClick={() => handleSelect(item)}
              >
                <span className="airportAutocomplete__code">{item.airline_code}</span>
                <span className="airportAutocomplete__details">
                  <span className="airportAutocomplete__city">{item.name}</span>
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default AirlineAutocomplete;
